import { apiClient, handleApiError } from '@/lib/api-client';

export interface CreditGrant {
  credits_remaining: number;
  expires_at: string | null;
  source: string;
}

export interface CreditBalance {
  balance: number;
  free: number;
  bought: number;
  total_purchased: number;
  total_used: number;
  download_allowed: boolean;
  free_reset_at?: string | null;
  free_reset_days?: number;
  free_weekly_limit?: number;
  next_reset?: string | null;
  grants?: CreditGrant[];
  next_paid_expires_at?: string | null;
}

export interface VoucherRedemption {
  ok: boolean;
  credits_added: number;
  new_balance: number;
  expires_at: string;
}

export interface CreditTransaction {
  _id: string;
  client_id: string;
  transaction_id: string;
  type: string;
  amount: number;
  balance_before: number;
  balance_after: number;
  reason?: string;
  created_at: string;
  created_by: string;
  job_id?: string;
  webhook_id?: string;
}

export interface JobCostSummary {
  job_id: string;
  filename: string;
  total_rows: number;
  processed_rows: number;
  status: string;
  total_credits: number;
  created_at: string | null;
}

export interface JobCostsResponse {
  jobs: JobCostSummary[];
}

export interface TransactionsResponse {
  transactions: CreditTransaction[];
  total: number;
  page: number;
  per_page: number;
}

class CreditsService {
  async getBalance(): Promise<CreditBalance> {
    try {
      const response = await apiClient.get<CreditBalance>('/api/v1/credits');
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  }

  async getTransactions(page = 1, perPage = 10): Promise<TransactionsResponse> {
    try {
      const response = await apiClient.get<TransactionsResponse>('/api/v1/credits/transactions', {
        params: { page, per_page: perPage },
      });
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  }

  async getJobCosts(): Promise<JobCostsResponse> {
    try {
      const response = await apiClient.get<JobCostsResponse>('/api/v1/credits/jobs');
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  }

  async topup(pkg: string): Promise<{ checkout_url: string }> {
    try {
      const response = await apiClient.post<{ checkout_url: string }>('/api/v1/credits/topup', { package: pkg });
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  }

  async batchCheckout(contacts: number, options: string[]): Promise<{ checkout_url: string }> {
    try {
      const response = await apiClient.post<{ checkout_url: string }>('/api/v1/credits/batch-checkout', {
        contacts,
        options,
      });
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  }

  async redeemVoucher(code: string): Promise<VoucherRedemption> {
    try {
      const response = await apiClient.post<VoucherRedemption>('/api/v1/credits/redeem', { code });
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  }

  async exportTransactions(startDate?: string, endDate?: string): Promise<void> {
    try {
      const response = await apiClient.get('/api/v1/credits/transactions/export', {
        params: {
          ...(startDate ? { start_date: startDate } : {}),
          ...(endDate ? { end_date: endDate } : {}),
        },
        responseType: 'blob',
      });
      const url = URL.createObjectURL(new Blob([response.data], { type: 'text/csv' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `knox_transactions_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (error) {
      throw handleApiError(error);
    }
  }
}

export const creditsService = new CreditsService();
